import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { setDemoTokenFallback, getApiBaseUrl } from '../../services/apiClient';
import { fetchCurrentUser } from '../../services/userService';
import { useAuthStore } from '../../stores/authStore';
import type { User, UserRole } from '../../types';

const DEMO_SESSION_TOKEN_KEY = 'demo_session_token';
const NEO4J_POLL_INTERVAL_MS = 3000;

interface DemoApiResponse {
  session_token: string;
  access_token: string;
  expires_in: number;
  neo4j_ready: boolean;
}

type SetupPhase = 'session' | 'indexing';

function DemoLoadingScreen({ phase }: { phase: SetupPhase }) {
  const { t } = useTranslation();
  return (
    <div className="flex h-screen w-full items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4 rounded-xl border border-border bg-card p-10 shadow-lg max-w-sm w-full text-center">
        <img src={`${import.meta.env.BASE_URL}logo.png`} alt="GenieFactory" className="h-10 mb-2" />
        <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden="true" />
        <p className="text-base font-medium text-foreground">
          {t('demo.preparing')}
        </p>
        {phase === 'indexing' && (
          <p className="text-sm text-muted-foreground">{t('demo.indexing')}</p>
        )}
      </div>
    </div>
  );
}

function DemoErrorScreen({ message }: { message: string }) {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-background">
      <div className="rounded-xl border border-destructive/40 bg-card p-10 shadow-lg max-w-sm w-full text-center">
        <p className="text-sm text-destructive font-medium">{message}</p>
      </div>
    </div>
  );
}

// Module-level lock prevents React StrictMode's double-invocation from creating two sessions.
let _setupInFlight = false;

export function DemoApp() {
  const navigate = useNavigate();
  const { actions } = useAuthStore();
  const [phase, setPhase] = useState<SetupPhase>('session');
  const [error, setError] = useState<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (_setupInFlight) return;
    _setupInFlight = true;
    cancelledRef.current = false;
    const baseUrl = getApiBaseUrl();

    const stopPolling = () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    const pollUntilReady = (sessionToken: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        pollTimerRef.current = setInterval(async () => {
          if (cancelledRef.current) {
            stopPolling();
            return;
          }
          try {
            const resp = await fetch(
              `${baseUrl}/platform/demo/session/status?session_token=${encodeURIComponent(sessionToken)}`
            );
            if (!resp.ok) throw new Error(`Status poll failed: ${resp.status}`);
            const data = await resp.json() as { neo4j_ready: boolean; is_active: boolean };
            if (data.neo4j_ready) {
              stopPolling();
              resolve();
            }
          } catch (err) {
            stopPolling();
            reject(err);
          }
        }, NEO4J_POLL_INTERVAL_MS);
      });
    };

    const setup = async () => {
      setPhase('session');

      let data: DemoApiResponse;

      const storedToken = localStorage.getItem(DEMO_SESSION_TOKEN_KEY);
      if (storedToken) {
        try {
          const resp = await fetch(`${baseUrl}/platform/demo/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_token: storedToken }),
          });
          if (!resp.ok) throw new Error(`Refresh failed: ${resp.status}`);
          data = await resp.json() as DemoApiResponse;
        } catch {
          localStorage.removeItem(DEMO_SESSION_TOKEN_KEY);
          const resp = await fetch(`${baseUrl}/platform/demo/session`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          });
          if (!resp.ok) throw new Error(`Session creation failed: ${resp.status}`);
          data = await resp.json() as DemoApiResponse;
        }
      } else {
        const resp = await fetch(`${baseUrl}/platform/demo/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
        if (!resp.ok) throw new Error(`Session creation failed: ${resp.status}`);
        data = await resp.json() as DemoApiResponse;
      }

      if (cancelledRef.current) return;

      // Persist session token, wire access token into apiClient (memory-only)
      localStorage.setItem(DEMO_SESSION_TOKEN_KEY, data.session_token);
      setDemoTokenFallback(data.access_token);

      // Populate authStore so the normal app works
      const backendUser = await fetchCurrentUser();
      const demoUser: User = {
        id: backendUser.id,
        email: backendUser.email,
        name: (backendUser as any).name ?? backendUser.email,
        role: (backendUser as any).role as UserRole,
        is_active: (backendUser as any).is_active,
        organization_id: (backendUser as any).organization_id,
        organization_is_active: (backendUser as any).organization_is_active,
        organization_is_demo: (backendUser as any).organization_is_demo ?? true,
        avatar_url: (backendUser as any).avatar_url,
        preferences: (backendUser as any).preferences,
        logto_id: (backendUser as any).logto_id,
        profile_id: (backendUser as any).profile_id ?? null,
        profile: (backendUser as any).profile ?? null,
      };
      actions.loginSuccess({ access_token: data.access_token }, demoUser);

      if (cancelledRef.current) return;

      if (!data.neo4j_ready) {
        setPhase('indexing');
        await pollUntilReady(data.session_token);
      }

      if (cancelledRef.current) return;

      navigate('/welcome', { replace: true });
    };

    setup().catch((err: unknown) => {
      if (cancelledRef.current) return;
      const message = err instanceof Error ? err.message : 'Demo setup failed';
      console.error('[DemoApp] setup error:', err);
      localStorage.removeItem(DEMO_SESSION_TOKEN_KEY);
      setDemoTokenFallback(null);
      setError(message);
    });

    return () => {
      cancelledRef.current = true;
      _setupInFlight = false;
      stopPolling();
    };
  }, [actions, navigate]);

  if (error) {
    return <DemoErrorScreen message={error} />;
  }

  return <DemoLoadingScreen phase={phase} />;
}
